import { useCallback, useState } from "react";
import { Button, FileUpload, Flex, ModalV2, Text, type FileObjectType } from "@jsq/ds/core";
import { Form, FormNumberInput, FormSelect, FormTextarea } from "@jsq/ds/forms";
import { Colors } from "@jsq/ds/styles";
import { Money, MoneyFormat } from "~/components/Money";
import { SPACING } from "~/styles/variables";
import {
  OVERRIDE_EUM_VALUE_LABEL,
  REASON_CODE_OPTIONS,
  SUPPORTED_FILE_TYPES,
} from "../constants";
import { usdDollarsToMoney } from "../utils/moneyUtils";
import type {
  OverrideEumValueFormValues,
  ValidatedOverrideEumValueFormValues,
  OverrideEumValueModalProps,
} from "./types";

const INITIAL_VALUES: OverrideEumValueFormValues = {
  newEumValue: null,
  reasonCode: null,
  description: "",
};

const isFormValid = (
  values: OverrideEumValueFormValues,
): values is ValidatedOverrideEumValueFormValues =>
  typeof values.newEumValue === "number" &&
  typeof values.reasonCode === "string" &&
  values.reasonCode.length > 0;

export const OverrideEumValueModal = ({
  isOpen,
  onClose,
  onSubmit,
  selectedEntityCount,
  currentEumValue,
}: OverrideEumValueModalProps) => {
  const [file, setFile] = useState<File | null>(null);

  const handleFileSelected = (selectedFiles: FileObjectType[]) => {
    const accepted = selectedFiles.find((f) => f.status === "accepted");
    setFile(accepted ? accepted.file : null);
  };

  const handleFileDelete = () => {
    setFile(null);
  };

  const handleClose = useCallback(() => {
    setFile(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(
    async (values: OverrideEumValueFormValues) => {
      if (isFormValid(values)) {
        await onSubmit({
          newEumValue: values.newEumValue,
          reasonCode: values.reasonCode,
          description: values.description.trim(),
          file,
        });
        setFile(null);
      }
    },
    [onSubmit, file],
  );

  if (!isOpen) {
    return null;
  }

  const entityLabel = selectedEntityCount === 1 ? "entity" : "entities";
  const currentEumMoney = usdDollarsToMoney(currentEumValue);

  return (
    <ModalV2
      show={isOpen}
      onHide={handleClose}
      title={OVERRIDE_EUM_VALUE_LABEL}
      description={`${selectedEntityCount} selected ${entityLabel}`}
      data-testid="override-eum-value-modal"
    >
      <Form<OverrideEumValueFormValues>
        initialValues={INITIAL_VALUES}
        onSubmit={handleSubmit}
        submitOnlyChangedValues={false}
      >
        <ModalV2.Body>
          <Flex flexDirection="column" gap={SPACING.LARGE}>
            <Flex flexDirection="column" gap={SPACING.X_SMALL}>
              <Text variant="secondary-sm" color={Colors.Neutral600}>
                Current EUM value
              </Text>
              <Text
                variant="strong-md"
                color={Colors.Neutral900}
                data-testid="current-eum-value"
              >
                <Money
                  money={currentEumMoney}
                  format={MoneyFormat.Symbol}
                  rounded
                  dashesAsDefault
                />
              </Text>
            </Flex>
            <FormNumberInput
              name="newEumValue"
              label="New EUM value"
              placeholder="0.00"
              preAdornment="$"
              min={0}
              hideSteps
              isFullWidth
            />
            <FormSelect
              name="reasonCode"
              label="Reason code"
              placeholder="Select option"
              data-testid="reason-code-select"
              full
            >
              <FormSelect.Section>
                {REASON_CODE_OPTIONS.map((option) => (
                  <FormSelect.Option key={option.value} value={option.value}>
                    {option.label}
                  </FormSelect.Option>
                ))}
              </FormSelect.Section>
            </FormSelect>
            <FormTextarea
              name="description"
              label="Description"
              placeholder="Add further details for this approval"
              size="small"
              isFullWidth
            />
            <Flex flexDirection="column" gap={SPACING.X_SMALL}>
              <Flex alignItems="center" gap={SPACING.X_SMALL}>
                <Text variant="strong-md" color={Colors.Neutral900}>
                  Supporting document
                </Text>
                <Text asSpan variant="regular-md" color={Colors.Neutral600}>
                  (optional)
                </Text>
              </Flex>
              <FileUpload
                isFullWidth
                size="small"
                canAllowMultiple={false}
                acceptedFileTypes={SUPPORTED_FILE_TYPES}
                onFileSelected={handleFileSelected}
                onDelete={handleFileDelete}
                data-testid="supporting-document-upload"
              />
            </Flex>
          </Flex>
        </ModalV2.Body>
        <ModalV2.Footer>
          <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
            <Button variant="neutral" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Submit for approval
            </Button>
          </Flex>
        </ModalV2.Footer>
      </Form>
    </ModalV2>
  );
};
